/* eslint-disable */
/* eslint-disable react/prop-types */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import type { SuggestionProps } from "@tiptap/suggestion";
import BubbleMenuExtension from "@tiptap/extension-bubble-menu";
import {
  Paper,
  ActionIcon,
  Group,
  Popover,
  Text,
  ScrollArea,
  Loader,
} from "@mantine/core";
import { Bold, Italic } from "tabler-icons-react";
import { BubbleMenu } from "@tiptap/react/menus";
import { useGetIssueHistory } from "api/issues/useGetIssueHistory";
import { User } from "types";
import { useCreateMentionNotification } from "api/comments/mention";

interface RawTiptapEditorProps {
  onChange: (html: string) => void;
  projectId: string | undefined;
  issueId: string;
  user: string;
  members: User<string>[];
  clearSignal?: number;
}

export function RawTiptapEditor({
  onChange,
  projectId,
  members,
  issueId,
  user,
  clearSignal,
}: RawTiptapEditorProps) {
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionItems, setMentionItems] = useState<
    { id: string; label: string }[]
  >([]);
  const [mentionRect, setMentionRect] = useState<DOMRect | null>(null);
  const [mentionReplaceRange, setMentionReplaceRange] = useState<{
    from: number;
    to: number;
  } | null>(null);

  const { mutate: notifyMentions } = useCreateMentionNotification();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useGetIssueHistory(projectId, issueId);
  const editorWrapperRef = useRef<HTMLDivElement | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const editor = useEditor(
    {
      extensions: [
        StarterKit,
        LinkExtension.configure({ openOnClick: false }),
        BubbleMenuExtension,
        Mention.configure({
          HTMLAttributes: { class: "mention" },
          renderLabel: ({ node }) => `@${String(node.attrs.label)}`,
          suggestion: {
            char: "@",
            startOfLine: false,
            items: ({ query }: { query: string }) => {
              const users = members
                .filter((u) => u._id !== user)
                .map((m) => ({
                  id: m._id,
                  label: `${m.firstName} ${m.lastName}`,
                }));

              return users
                .filter((u) =>
                  u.label.toLowerCase().startsWith(query.toLowerCase())
                )
                .slice(0, 5);
            },
            render: () => {
              let suggestionProps: SuggestionProps;
              return {
                onStart: (props: SuggestionProps) => {
                  suggestionProps = props;
                  setMentionItems(props.items);
                  setMentionOpen(true);
                  setMentionRect(props.clientRect!());
                  setMentionReplaceRange(props.range);
                },
                onUpdate: (props: SuggestionProps) => {
                  suggestionProps = props;
                  setMentionItems(props.items);
                  setMentionRect(props.clientRect!());
                  setMentionReplaceRange(props.range);
                },
                onKeyDown: () => false,
                onExit: () => {
                  setMentionOpen(false);
                  setMentionItems([]);
                  setMentionRect(null);
                  setMentionReplaceRange(null);
                },

                command: ({ editor, range, props }: any) => {
                  const useRange = range ?? mentionReplaceRange;
                  if (!useRange) {
                    editor
                      .chain()
                      .focus()
                      .insertContent([
                        { type: "mention", attrs: props },
                        { type: "text", text: " " },
                      ])
                      .run();
                  } else {
                    editor
                      .chain()
                      .focus()
                      .insertContentAt(useRange, [
                        { type: "mention", attrs: props },
                        { type: "text", text: " " },
                      ])
                      .run();
                  }
                  setMentionOpen(false);
                  setMentionItems([]);
                  setMentionRect(null);
                  setMentionReplaceRange(null);
                },
              };
            },
          },
        }),
      ],
      content: `<div>Type <strong>@</strong> to mention someone</div>`,
    },
    [members]
  );

  useEffect(() => {
    if (!editor) {
      return () => {};
    }

    const handler = () => {
      onChange(editor.getHTML());
    };

    editor.on("update", handler);

    return () => {
      editor.off("update", handler);
    };
  }, [editor, onChange]);
  useEffect(() => {
    if (editor && typeof clearSignal !== "undefined") {
      editor.commands.setContent("");
      onChange("");
    }
  }, [clearSignal]);
  useLayoutEffect(() => {
    if (mentionRect && editorWrapperRef.current) {
      const box = editorWrapperRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: mentionRect.bottom - box.top + 2,
        left: mentionRect.left - box.left,
      });
    }
  }, [mentionRect]);

  if (!editor) {
    return null;
  }

  const insertMention = (item: { id: string; label: string }) => {
    if (!mentionReplaceRange) {
      editor
        .chain()
        .focus()
        .insertContent([
          { type: "mention", attrs: item },
          { type: "text", text: " " },
        ])
        .run();
      setMentionOpen(false);
      setMentionItems([]);
      setMentionReplaceRange(null);
      setMentionRect(null);
      return;
    }

    editor
      .chain()
      .focus()
      .insertContentAt(mentionReplaceRange, [
        { type: "mention", attrs: item },
        { type: "text", text: " " },
      ])
      .run();

    setMentionOpen(false);
    setMentionItems([]);
    setMentionReplaceRange(null);
    setMentionRect(null);
  };

  return (
    <Paper
      ref={editorWrapperRef}
      style={{ position: "relative", width: "100%" }}
      sx={(theme) => ({
        ".ProseMirror": {
          minHeight: 50,
          padding: theme.spacing.sm,
          borderRadius: theme.radius.sm,
          outline: "none",
          border: `1px solid gray`,
        },

        ".ProseMirror:focus": {
          outline: `1px solid #7950f2`,
        },
      })}
    >
      <BubbleMenu editor={editor}>
        <Paper px="xs" py={4} withBorder shadow="md" radius="sm">
          <Group spacing={4}>
            <ActionIcon
              variant={editor.isActive("bold") ? "filled" : "subtle"}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <Bold size={16} />
            </ActionIcon>
            <ActionIcon
              variant={editor.isActive("italic") ? "filled" : "subtle"}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <Italic size={16} />
            </ActionIcon>
          </Group>
        </Paper>
      </BubbleMenu>

      <Popover
        opened={mentionOpen}
        onClose={() => setMentionOpen(false)}
        withinPortal
        position="bottom-start"
        trapFocus={false}
        shadow="sm"
      >
        <Popover.Target>
          <div
            style={{
              position: "absolute",
              left: popoverPosition.left,
              top: popoverPosition.top,
              width: 0,
              height: 0,
              pointerEvents: "none",
            }}
          />
        </Popover.Target>

        <Popover.Dropdown p={0} sx={{ border: 0, overflow: "hidden" }}>
          <ScrollArea style={{ maxHeight: 180 }} scrollbarSize={6}>
            {mentionItems.length === 0 ? (
              <Group p="sm" h={60} align="center" position="center">
                <Loader size="sm" />
              </Group>
            ) : (
              mentionItems.map((item) => (
                <Text
                  key={item.id}
                  sx={(theme) => ({
                    fontSize: theme.fontSizes.sm,
                    padding: theme.spacing.sm,
                    cursor: "pointer",
                    borderRadius: theme.radius.sm,
                    transition: "background-color 150ms ease",
                    "&:hover": {
                      backgroundColor: theme.colors.blue[1],
                    },
                    "&:active": {
                      backgroundColor: theme.colors.blue[1],
                    },
                  })}
                  onMouseDown={(e: any) => {
                    e.preventDefault();
                    editor.chain().focus().run();
                    insertMention(item);
                  }}
                >
                  {item.label}
                </Text>
              ))
            )}
          </ScrollArea>
        </Popover.Dropdown>
      </Popover>

      <EditorContent editor={editor} />
    </Paper>
  );
}
